/**
 * Point Draw connection-point classification.
 *
 * A visually continuous track may be stored as many abutting rail primitives.
 * Point Draw must only snap to *logical* connection points the user can see
 * (open chain ends, circular-junction ports) — never to internal segment
 * joints created by implementation splitting.
 *
 * This module is Point-Draw-only; topology / routing / rendering are unchanged.
 */
import { LayoutElement } from '../types';
import {
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  Pt,
  getRailEndpoints,
  isJunctionRailType,
  projectPointToNearestRail,
  resolveRailType,
} from './topology';

/** Open chain end or junction port — valid Point Draw snap target. */
export type LogicalEndpoint = {
  kind: 'logical';
  tip: Pt;
  railId: string;
  /** 'rail-end' = degree-1 straight/curve tip; 'junction-port' = circle-junction spoke. */
  role: 'rail-end' | 'junction-port';
};

/** Shared joint / mid-chain tip — never a Point Draw snap target. */
export type InternalGeometryEndpoint = {
  kind: 'internal';
  tip: Pt;
  railId: string;
};

export type ClassifiedEndpoint = LogicalEndpoint | InternalGeometryEndpoint;

export type PointDrawEndpointOptions = {
  /** Rails to ignore (e.g. the run currently being drawn). */
  excludeIds?: ReadonlySet<string>;
  /** Tip clustering distance (defaults to ENDPOINT_TOLERANCE). */
  tipTolerance?: number;
  /** How close a tip may sit on another rail's interior before it is internal. */
  interiorTolerance?: number;
};

type TipRecord = {
  railId: string;
  tip: Pt;
  isJunctionPort: boolean;
};

const collectTips = (
  rails: LayoutElement[],
  excludeIds?: ReadonlySet<string>
): TipRecord[] => {
  const tips: TipRecord[] = [];
  for (const el of rails) {
    if (!el.metadata.visible || !ALL_RAIL_TYPES.has(el.type)) continue;
    if (excludeIds?.has(el.id)) continue;
    const rt = resolveRailType(el);
    const isJunctionPort = isJunctionRailType(rt);
    for (const tip of getRailEndpoints(el)) {
      tips.push({ railId: el.id, tip, isJunctionPort });
    }
  }
  return tips;
};

const tipsNear = (a: Pt, b: Pt, tol: number): boolean =>
  Math.hypot(a.x - b.x, a.y - b.y) <= tol;

/**
 * True when `tip` lies on the interior of another rail's centerline (not merely
 * at that rail's own tip). Catches collinear splits that failed to share an
 * exact endpoint within tipTolerance.
 */
const tipLiesOnOtherRailInterior = (
  tip: Pt,
  ownRailId: string,
  rails: LayoutElement[],
  excludeIds: ReadonlySet<string> | undefined,
  tipTol: number,
  interiorTol: number
): boolean => {
  const others = rails.filter(
    (el) =>
      el.metadata.visible &&
      ALL_RAIL_TYPES.has(el.type) &&
      el.id !== ownRailId &&
      !excludeIds?.has(el.id) &&
      !isJunctionRailType(resolveRailType(el))
  );
  const proj = projectPointToNearestRail(others, tip, interiorTol);
  if (!proj) return false;

  // Near another rail's tip → endpoint clustering handles it; not "interior".
  const host = rails.find((el) => el.id === proj.railId);
  if (!host) return false;
  for (const end of getRailEndpoints(host)) {
    if (tipsNear(tip, end, tipTol)) return false;
  }
  return true;
};

/**
 * Classify every rail tip for Point Draw. Junction ports are always logical.
 * Straight/curve tips are logical only when they are open chain ends (no other
 * tip shares the cluster AND the tip is not glued to another rail's interior).
 */
export function classifyPointDrawEndpoints(
  rails: LayoutElement[],
  options: PointDrawEndpointOptions = {}
): ClassifiedEndpoint[] {
  const tipTol = options.tipTolerance ?? ENDPOINT_TOLERANCE;
  const interiorTol = options.interiorTolerance ?? ENDPOINT_TOLERANCE;
  const tips = collectTips(rails, options.excludeIds);
  const out: ClassifiedEndpoint[] = [];

  for (const rec of tips) {
    if (rec.isJunctionPort) {
      out.push({
        kind: 'logical',
        tip: rec.tip,
        railId: rec.railId,
        role: 'junction-port',
      });
      continue;
    }

    const sharedWithOther = tips.some(
      (other) =>
        other.railId !== rec.railId && tipsNear(rec.tip, other.tip, tipTol)
    );

    const onInterior =
      !sharedWithOther &&
      tipLiesOnOtherRailInterior(
        rec.tip,
        rec.railId,
        rails,
        options.excludeIds,
        tipTol,
        interiorTol
      );

    if (sharedWithOther || onInterior) {
      out.push({ kind: 'internal', tip: rec.tip, railId: rec.railId });
    } else {
      out.push({
        kind: 'logical',
        tip: rec.tip,
        railId: rec.railId,
        role: 'rail-end',
      });
    }
  }

  return out;
}

/** Logical connection points only (open ends + junction ports). */
export function getLogicalPointDrawEndpoints(
  rails: LayoutElement[],
  options: PointDrawEndpointOptions = {}
): LogicalEndpoint[] {
  return classifyPointDrawEndpoints(rails, options).filter(
    (e): e is LogicalEndpoint => e.kind === 'logical'
  );
}

/**
 * True when `worldPt` sits on an existing rail centerline away from every
 * logical connection point — i.e. the occupied middle of a track (including
 * internal segment boundaries). Used to reject Point Draw clicks that would
 * create overlapping geometry.
 */
export function isOccupiedTrackInterior(
  worldPt: Pt,
  rails: LayoutElement[],
  options: PointDrawEndpointOptions & { perpTolerance: number } 
): boolean {
  const excludeIds = options.excludeIds;
  const tipTol = options.tipTolerance ?? ENDPOINT_TOLERANCE;
  const candidates = rails.filter(
    (el) =>
      el.metadata.visible &&
      ALL_RAIL_TYPES.has(el.type) &&
      !excludeIds?.has(el.id)
  );
  const proj = projectPointToNearestRail(candidates, worldPt, options.perpTolerance);
  if (!proj) return false;

  const logical = getLogicalPointDrawEndpoints(rails, options);
  const nearLogical = logical.some((e) => tipsNear(worldPt, e.tip, tipTol));
  // Also treat proximity to the projected point's distance to a logical tip.
  const projNearLogical = logical.some((e) => tipsNear({ x: proj.x, y: proj.y }, e.tip, tipTol));
  if (nearLogical || projNearLogical) return false;

  return true;
}
